import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiUrl } from '../../config'
import { getAuthHeaders, useAuthStore } from '@/stores/authStore'
import './ShopReviewDrawer.css'

interface Review {
  id: string
  reviewerId: string
  reviewerName: string
  rating: number
  text: string
  createdAt: string
  verified: boolean
}

interface ReviewSummary {
  average: number
  count: number
}

interface Props {
  shopId: string
  shopName?: string
  onClose: () => void
}

function Stars({ rating, size = 'md' }: { rating: number; size?: 'sm' | 'md' }) {
  const cls = size === 'sm' ? 'srd-mini-star' : 'srd-star'
  return (
    <div className={size === 'sm' ? 'srd-review-mini-stars' : 'srd-stars-row'}>
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={`${cls} ${s <= Math.round(rating) ? 'filled' : 'empty'}`}>★</span>
      ))}
    </div>
  )
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days < 1) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

export function ShopReviewDrawer({ shopId, shopName, onClose }: Props) {
  const currentUser = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const [summary, setSummary] = useState<ReviewSummary>({ average: 0, count: 0 })
  const [myReview, setMyReview] = useState<Review | null>(null)
  const [loading, setLoading] = useState(true)
  const [pickedRating, setPickedRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const fetchReviews = useCallback(async () => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${encodeURIComponent(shopId)}/public-reviews?limit=50&offset=0`))
      if (!res.ok) return
      const data = await res.json()
      setSummary(data.summary)
      if (currentUser) {
        const mine = (data.reviews as Review[]).find((r) => r.reviewerId === currentUser.id)
        setMyReview(mine ?? null)
      }
    } catch {}
    setLoading(false)
  }, [shopId, currentUser])

  useEffect(() => { fetchReviews() }, [fetchReviews])

  const handleSubmit = async () => {
    if (pickedRating === 0 || submitting) return
    setSubmitting(true)
    try {
      const res = await fetch(apiUrl(`/api/shops/${encodeURIComponent(shopId)}/public-reviews`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ rating: pickedRating, text: reviewText.trim() }),
      })
      if (res.ok) {
        setSubmitted(true)
        await fetchReviews()
      } else if (res.status === 409) {
        await fetchReviews()
      }
    } catch {}
    setSubmitting(false)
  }

  const displayRating = hoverRating || pickedRating
  const alreadyReviewed = !!myReview

  return (
    <div className="srd-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="srd-drawer">
        <div className="srd-handle" />
        <div className="srd-header">
          <h2 className="srd-title">Reviews{shopName ? ` · ${shopName}` : ''}</h2>
          <button className="srd-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="srd-body">
          {/* Overall rating summary */}
          {!loading && summary.count > 0 && (
            <div className="srd-summary">
              <div className="srd-summary-avg">{summary.average.toFixed(1)}</div>
              <div className="srd-summary-right">
                <Stars rating={summary.average} />
                <div className="srd-count">{summary.count} review{summary.count !== 1 ? 's' : ''}</div>
              </div>
            </div>
          )}

          {/* My review — shown if user has reviewed */}
          {!loading && myReview && (
            <div className="srd-my-review">
              <div className="srd-my-review-label">My Review</div>
              <div className="srd-review-card srd-review-card--own">
                <div className="srd-review-top">
                  <span className="srd-reviewer-name">{myReview.reviewerName || 'You'}</span>
                  <Stars rating={myReview.rating} size="sm" />
                </div>
                <div className="srd-review-date">{timeAgo(myReview.createdAt)}</div>
                {myReview.text && <div className="srd-review-text">{myReview.text}</div>}
              </div>
            </div>
          )}

          {/* Write review — only if not already reviewed */}
          {!loading && !alreadyReviewed && (
            <div className="srd-form">
              <div className="srd-form-title">Leave a review</div>
              {submitted ? (
                <div className="srd-success">✓ Thank you! Your review was submitted.</div>
              ) : (
                <>
                  <div className="srd-rating-pick">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        className={`srd-rating-star${s <= displayRating ? ' active' : ''}`}
                        onMouseEnter={() => setHoverRating(s)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setPickedRating(s)}
                        aria-label={`${s} star`}
                      >★</button>
                    ))}
                  </div>
                  <textarea
                    className="srd-textarea"
                    placeholder="Share your experience… (optional)"
                    rows={3}
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                  />
                  <button
                    className="srd-submit"
                    onClick={handleSubmit}
                    disabled={pickedRating === 0 || submitting}
                  >
                    {submitting ? 'Submitting…' : 'Submit Review'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* View all reviews button */}
          <button
            className="srd-view-all"
            onClick={() => { onClose(); navigate(`/reviews/${shopId}`) }}
          >
            View all reviews
            {summary.count > 0 && <span className="srd-view-all-count">{summary.count}</span>}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
